<!--
 @description Top navigation bar rendered during an active game session.
 Contains the app logo and a hamburger-triggered SideMenu organised into three
 semantic groups (sidemenu-redesign spec):

   - "This game" (always visible): Menu du jeu, Quitter vers l'accueil.
   - "General" (always visible, including anonymous): Comment jouer?, Paramètres.
   - "This challenge" (gated: authStore.firebaseUser && gameStore.currentChallenge):
       Signaler un problème, Exclure ce défi. Absent from DOM when gate fails (AC-03, AC-04).

 Each group is a `<section aria-label="...">` ARIA landmark (AC-19). Every row
 carries a leading Flaticon uicons-solid-straight icon and meets the 44×44 px
 touch-target minimum (C-09, AC-06). Sign-out is NOT present in the SideMenu —
 it lives exclusively in Parameters.vue (sidemenu-redesign spec C-06).

 Opens Parameters and Rules inside a full-screen Modal, the Feedback form in a
 separate responsive Modal, and the blacklist confirmation dialog when "Exclure
 ce défi" is tapped (challenge-blacklist-frontend spec). The CapReachedDialog
 appears if the blacklist cap is already reached (AC-05).

 Focus management: the hamburger toggle (`hamburgerRef`) receives focus when the
 SideMenu closes; the SideMenu close button (`sideMenuRef.closeButtonEl`) receives
 focus when the SideMenu opens (DEC-003, AC-09). ESC closes the SideMenu and
 returns focus to the hamburger.

 @props
   onAutoRoll — Optional callback invoked after a successful challenge blacklist
                so the parent (Game.vue) can advance to the next challenge.
                Alternatively the component emits `auto-roll` if the prop is absent.
                DEC-035 (challenge-blacklist-frontend): auto-roll boundary via prop.
   cap        — Optional numeric cap; when challengeBlacklist.length >= cap the
                CapReachedDialog opens instead of the confirmation dialog (AC-05).
                DEC-038: cap accepted as a prop following DEC-009 precedent.

 @emits
   auto-roll — Emitted after a successful blacklist when onAutoRoll prop is absent.

 @example
 <GameNavbar :onAutoRoll="autoRollAfterBlacklist" :cap="blacklistCap" />

 @accessibility Hamburger toggle uses <button> (not <div>) with aria-expanded and
 aria-controls="game-side-menu" (DEC-004). Close button uses <button> wrapping the
 icon with aria-hidden on the <i> (DEC-005). Groups are <section aria-label="...">
 with keys from game.sideMenu.aria.* (DEC-006). ESC closes the SideMenu; Tab order
 follows DOM source order top-to-bottom within each group (AC-10).
-->
<template>
  <header ref="headerRef" class="game-navbar">
    <nav aria-label="Main Navigation" class="game-navbar__nav">
      <div class="game-navbar__desktop-nav">
        <div class="game-navbar__branding">
          <router-link :to="{ name: 'home' }" class="no-active" aria-label="Home"
            ><img src="/Logo-App.svg" alt="Logo Allez! Allez!"
          /></router-link>
        </div>
      </div>
      <div ref="icon" class="game-navbar__icon">
        <button
          ref="hamburgerRef"
          type="button"
          class="game-navbar__hamburger game-navbar__hamburger--btn no-style-button"
          :class="{ 'game-navbar__hamburger--active': sideMenuOpen }"
          :aria-label="t('game.sideMenu.aria.toggle')"
          :aria-expanded="sideMenuOpen"
          aria-controls="game-side-menu"
          @click="openMenu"
        >
          <span class="game-navbar__hamburger-line--1"></span>
          <span class="game-navbar__hamburger-line--2"></span>
          <span class="game-navbar__hamburger-line--3"></span>
        </button>
      </div>
    </nav>

    <SideMenu
      id="game-side-menu"
      ref="sideMenuRef"
      :isOpen="sideMenuOpen"
      @close="closeMenu"
      class="slider"
    >
      <div class="game-navbar__menu-options">
        <!--
        Group 1 — "This game"
        Always rendered when the SideMenu is open (SideMenu only lives in game routes).
      -->
        <section class="side-menu__group" :aria-label="t('game.sideMenu.aria.thisGame')">
          <router-link
            class="side-menu__row side-menu__row--link"
            :to="{ name: 'main-menu' }"
            :aria-label="t('game.nav.main-menu')"
            @click="closeMenu"
          >
            <i class="fi fi-ss-list side-menu__row-icon" aria-hidden="true"></i>
            <span class="side-menu__row-label">{{ $t('game.nav.main-menu') }}</span>
          </router-link>

          <router-link
            class="side-menu__row side-menu__row--link"
            :to="{ name: 'home' }"
            :aria-label="t('game.nav.quit')"
            @click="closeMenu"
          >
            <i class="fi fi-ss-exit side-menu__row-icon" aria-hidden="true"></i>
            <span class="side-menu__row-label">{{ $t('game.nav.quit') }}</span>
          </router-link>
        </section>

        <!--
        Group 2 — "General"
        Always rendered, including for anonymous players.
      -->
        <section class="side-menu__group" :aria-label="t('game.sideMenu.aria.general')">
          <button
            type="button"
            class="side-menu__row"
            :aria-label="t('pages.rules.title')"
            @click="openRules"
          >
            <i class="fi fi-ss-book-alt side-menu__row-icon" aria-hidden="true"></i>
            <span class="side-menu__row-label">{{ $t('pages.rules.title') }}</span>
          </button>

          <button
            type="button"
            class="side-menu__row"
            :aria-label="t('pages.parameters.title')"
            @click="openParameters"
          >
            <i class="fi fi-ss-settings side-menu__row-icon" aria-hidden="true"></i>
            <span class="side-menu__row-label">{{ $t('pages.parameters.title') }}</span>
          </button>
        </section>
        <!--
        Group 3 — "This challenge"
        Rendered only when signed-in AND an active challenge is present (C-11).
        Both rows share the same visibility gate uniformly (AC-03, AC-04).
      -->
        <section
          v-if="authStore.firebaseUser && gameStore.currentChallenge"
          class="side-menu__group"
          :aria-label="t('game.sideMenu.aria.thisChallenge')"
        >
          <button
            type="button"
            class="side-menu__row"
            :aria-label="t('game.nav.feedback')"
            @click="openFeedback"
          >
            <i class="fi fi-ss-comment-alt side-menu__row-icon" aria-hidden="true"></i>
            <span class="side-menu__row-label">{{ $t('game.nav.feedback') }}</span>
          </button>
          <button
            type="button"
            class="side-menu__row"
            :aria-label="t('challengeBlacklist.sideMenuItem')"
            @click="handleBlacklistTap"
          >
            <i class="fi fi-ss-ban side-menu__row-icon" aria-hidden="true"></i>
            <span class="side-menu__row-label">{{ $t('challengeBlacklist.sideMenuItem') }}</span>
          </button>
        </section>
      </div>
    </SideMenu>

    <Modal
      :fullScreen="isMobileDevice()"
      :isOpen="feedbackModalOpen"
      @close="feedbackModalOpen = false"
    >
      <FeedbackForm
        :game="gameStore.game"
        :challenge="gameStore.currentChallenge"
        @close="feedbackModalOpen = false"
      />
    </Modal>

    <Modal :full-screen="true" :isOpen="modalOpen" @close="closeModal">
      <Parameters :noBackground="true" v-if="parametersOpen" class="game-navbar__modal-content" />
      <Rules :inModal="true" v-if="rulesOpen" class="game-navbar__modal-content" />
    </Modal>

    <!-- Blacklist confirmation dialog (AC-02 / AC-03 / AC-04) -->
    <!-- v-if guards the slot content so it is absent from the DOM when closed,
         because the Modal stub in tests renders slot content unconditionally. -->
    <Modal
      v-if="confirmDialogOpen"
      :isOpen="confirmDialogOpen"
      :isClosable="true"
      :isOutsideClosable="true"
      @close="confirmDialogOpen = false"
    >
      <div class="game-navbar__confirm-dialog">
        <h2>{{ $t('challengeBlacklist.confirmDialogTitle') }}</h2>
        <p>{{ $t('challengeBlacklist.confirmDialogBody') }}</p>
        <div class="game-navbar__confirm-actions">
          <button type="button" class="primary" @click="handleBlacklistConfirm">
            {{ $t('challengeBlacklist.confirmDialogPrimary') }}
          </button>
          <button type="button" @click="confirmDialogOpen = false">
            {{ $t('challengeBlacklist.confirmDialogSecondary') }}
          </button>
        </div>
      </div>
    </Modal>

    <!-- Cap-reached dialog (AC-05 / AC-06) -->
    <!-- v-if guards so CapReachedDialog content is absent when closed. -->
    <CapReachedDialog
      v-if="capReachedDialogOpen"
      :isOpen="capReachedDialogOpen"
      @close="capReachedDialogOpen = false"
    />
  </header>
</template>

<script setup lang="ts">
import SideMenu from '@/components/common/Global/side-menu/SideMenu.vue'
import Modal from '@/components/common/Global/modal/Modal.vue'
import Parameters from '@/views/Website/parameters/Parameters.vue'
import Rules from '@/views/Website/rules/Rules.vue'
import FeedbackForm from '@/components/Game/feedback/FeedbackForm.vue'
import CapReachedDialog from '@/views/Website/parameters/CapReachedDialog.vue'

import { nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGameStore } from '@/stores/gameStore'
import { useAuthStore } from '@/stores/authStore'
import { updateChallengeBlacklist } from '@/stores/users'
import { isMobileDevice } from '@/stores/methods'

// ---------------------------------------------------------------------------
// Props / emits (DEC-035: auto-roll via onAutoRoll prop; DEC-038: cap as prop)
// ---------------------------------------------------------------------------

interface GameNavbarProps {
  /**
   * Callback invoked after a successful challenge blacklist so the parent
   * (Game.vue) can advance to the next challenge. DEC-035.
   */
  onAutoRoll?: () => void
  /**
   * Optional cap override. When challengeBlacklist.length >= cap the
   * CapReachedDialog opens instead of the confirmation dialog (AC-05).
   * DEC-038: exposed as a prop following DEC-009 precedent.
   */
  cap?: number
}

const props = withDefaults(defineProps<GameNavbarProps>(), {
  onAutoRoll: undefined,
  cap: undefined
})

const emit = defineEmits<{
  /** Emitted after a successful blacklist when onAutoRoll prop is absent. DEC-035. */
  'auto-roll': []
}>()

// ---------------------------------------------------------------------------
// Stores / composables
// ---------------------------------------------------------------------------

const gameStore = useGameStore()
const authStore = useAuthStore()
const { t } = useI18n()

// ---------------------------------------------------------------------------
// Reactive state
// ---------------------------------------------------------------------------

const isMobile = ref(false)
const sideMenuOpen = ref(false)
const headerRef = ref<HTMLElement | undefined>()
const modalOpen = ref(false)
const parametersOpen = ref(false)
const rulesOpen = ref(false)
const feedbackModalOpen = ref(false)
const confirmDialogOpen = ref(false)
const capReachedDialogOpen = ref(false)

// ---------------------------------------------------------------------------
// Focus management refs (AC-09)
// hamburgerRef — receives focus when the SideMenu closes (or ESC is pressed).
// sideMenuRef  — exposes closeButtonEl so we can focus it when opening.
// ---------------------------------------------------------------------------

const hamburgerRef = ref<HTMLButtonElement | undefined>()
const sideMenuRef = ref<InstanceType<typeof SideMenu> | undefined>()

// ---------------------------------------------------------------------------
// Cap computation (DEC-038 / DEC-036)
// ---------------------------------------------------------------------------

function effectiveCap(): number {
  if (props.cap !== undefined) return props.cap
  return Infinity
}

// ---------------------------------------------------------------------------
// Menu helpers
// ---------------------------------------------------------------------------

/**
 * Opens the SideMenu and, after the DOM has settled, moves focus to the
 * close button (AC-09 open-focus).
 */
async function openMenu() {
  sideMenuOpen.value = true
  await nextTick()
  sideMenuRef.value?.closeButtonEl?.focus()
}

/**
 * Closes the SideMenu and returns focus to the hamburger toggle (AC-09
 * close-focus). Called on close event, ESC (delegated from SideMenu), and
 * router-link clicks.
 */
function closeMenu() {
  sideMenuOpen.value = false
  nextTick(() => {
    hamburgerRef.value?.focus()
  })
}

function openFeedback() {
  feedbackModalOpen.value = true
  closeMenu()
}

function openParameters() {
  parametersOpen.value = true
  modalOpen.value = true
  closeMenu()
}

function openRules() {
  rulesOpen.value = true
  modalOpen.value = true
  closeMenu()
}

function closeModal() {
  modalOpen.value = false
  parametersOpen.value = false
  rulesOpen.value = false
}

function checkScreenSize() {
  if (window.innerWidth < 990) {
    isMobile.value = true
    return
  }
  isMobile.value = false
  return
}

// ---------------------------------------------------------------------------
// Blacklist tap handler (AC-05: cap branch; otherwise: open confirmation)
// ---------------------------------------------------------------------------

function handleBlacklistTap(): void {
  const blacklist = authStore.dbUser?.challengeBlacklist ?? []
  if (blacklist.length >= effectiveCap()) {
    capReachedDialogOpen.value = true
    return
  }
  confirmDialogOpen.value = true
}

// ---------------------------------------------------------------------------
// Blacklist confirm handler (AC-03)
// ---------------------------------------------------------------------------

async function handleBlacklistConfirm(): Promise<void> {
  const uid = authStore.firebaseUser?.uid
  const currentId = gameStore.currentChallenge?.id
  if (!uid || !currentId) return

  const prev = authStore.dbUser?.challengeBlacklist ?? []
  const updated = [...prev, currentId]

  await attemptBlacklistWrite(uid, updated, prev, false)
}

async function attemptBlacklistWrite(
  uid: string,
  updated: string[],
  prev: string[],
  isRetry: boolean
): Promise<void> {
  try {
    await updateChallengeBlacklist(uid, updated)
    await authStore.updateDbUser(authStore.$state)
    confirmDialogOpen.value = false
    if (props.onAutoRoll) {
      props.onAutoRoll()
    } else {
      emit('auto-roll')
    }
  } catch (err: unknown) {
    const code = (err as { code?: string })?.code ?? (err as { message?: string })?.message ?? ''

    if (code === 'cap-exceeded') {
      confirmDialogOpen.value = false
      capReachedDialogOpen.value = true
      return
    }

    if (code === 'aborted' && !isRetry) {
      await attemptBlacklistWrite(uid, updated, prev, true)
      return
    }

    confirmDialogOpen.value = false
    // WHY: prev is the authoritative pre-mutation state; local state is unchanged on this
    // error path — void silences the unused-variable lint without introducing real logic.
    void prev
  }
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

onMounted(async () => {
  checkScreenSize()
  window.addEventListener('resize', checkScreenSize)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkScreenSize)
})
</script>

<style scoped lang="scss">
// ---------------------------------------------------------------------------
// Block: game-navbar
// The <header> root carries the game-navbar class.
// ---------------------------------------------------------------------------

.game-navbar {
  background-color: var(--background-light);
  z-index: 99;
  width: 100%;
  position: sticky;
  color: var(--text);
  top: 0;

  &__nav {
    @extend %flex;
    padding: 0;
    width: 90%;
    margin: 0 auto;

    @media (min-width: 1140px) {
      max-width: 1140px;
    }

    ul {
      font-weight: 600;
      color: var(--text);
    }

    li {
      text-transform: uppercase;
    }
  }

  &__desktop-nav {
    @extend %flex;
  }

  &__branding {
    @extend %flex;
    width: 225px;
    height: 50px;
    margin: 0;

    img {
      height: 32px;
    }
  }

  &__icon {
    @extend %flex;
    height: 100%;
    width: fit-content;
    margin-right: $half-spacing;
  }

  &__hamburger--active {
    .game-navbar__hamburger-line--1 {
      transform: rotate(45deg);
    }

    .game-navbar__hamburger-line--2 {
      transform: scaleY(0);
    }

    .game-navbar__hamburger-line--3 {
      transform: rotate(-45deg);
    }
  }
}

// ---------------------------------------------------------------------------
// Hamburger: game-navbar__hamburger (block element) and its sub-elements.
// The --btn modifier resets UA <button> defaults so the element renders
// identically to the original <div> while gaining keyboard focus + activation
// (WCAG 2.1.1). The 44×44 px minimum touch target is met by explicit sizing
// on the element (AC-06 / C-09).
// ---------------------------------------------------------------------------

.game-navbar__hamburger {
  @extend %flex;
  flex-direction: column;
  height: 20px;
  width: 25px;
  z-index: 2;

  &:hover {
    cursor: pointer;

    .game-navbar__hamburger-line--1,
    .game-navbar__hamburger-line--2,
    .game-navbar__hamburger-line--3 {
      background: var(--secondary);
    }
  }

  // --btn modifier: strips UA <button> chrome and enforces the 44 px touch area.
  &--btn {
    background: none;
    border: 0;
    border-radius: 0;
    padding: 0;
    margin: 0;
    font: inherit;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;

    // Keep the span lines at 25 × 4 px within the 44 px tap area.
    .game-navbar__hamburger-line--1,
    .game-navbar__hamburger-line--2,
    .game-navbar__hamburger-line--3 {
      width: 25px;
    }

    &:focus-visible {
      outline: 2px solid var(--primary);
      outline-offset: 4px;
      border-radius: 4px;
    }
  }
}

.game-navbar__hamburger-line--1,
.game-navbar__hamburger-line--2,
.game-navbar__hamburger-line--3 {
  display: block;
  height: 4px;
  width: 100%;
  border-radius: $small-border-radius;
  background: var(--secondary);
}

.game-navbar__hamburger-line--1 {
  transform-origin: 0% 0%;
  transition: transform 0.4s ease-in-out;
}

.game-navbar__hamburger-line--2 {
  transition: transform 0.2s ease-in-out;
}

.game-navbar__hamburger-line--3 {
  transform-origin: 0% 100%;
  transition: transform 0.4s ease-in-out;
}

// ---------------------------------------------------------------------------
// SideMenu options container
// ---------------------------------------------------------------------------

.game-navbar__menu-options {
  padding: 75px 0 50px 0;
  gap: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

// ---------------------------------------------------------------------------
// SideMenu grouped layout
// side-menu__* classes are kept as flat BEM classes (block = side-menu)
// because all three spec files reference them by name and they are authored
// within this component's scoped block.
// ---------------------------------------------------------------------------

/*
 * Each group is a <section aria-label="..."> landmark. No visible heading.
 * Groups are separated by whitespace ($double-spacing = 2rem) only — no
 * dividers, no background change (C-07, DEF-B).
 */
.side-menu__group {
  display: flex;
  flex-direction: column;
  padding: 0 $base-spacing;
  gap: 10px;

  & + .side-menu__group {
    margin-top: $double-spacing;
  }
}

/*
 * Each row is either a <button> or <router-link> with a leading icon and a
 * label. Minimum 44×44 px touch target enforced via min-height (C-09, AC-06).
 */
.side-menu__row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: $base-spacing;
  width: 100%;
  padding: $half-spacing 0;
  text-decoration: none;
  text-transform: uppercase;
  font-weight: 500;
  color: var(--text);
  font-size: $base-font-size;
  cursor: pointer;

  // Reset UA button chrome.
  background: none;
  border: 0;
  border-radius: 0;
  margin: 0;
  font-family: inherit;
  text-align: left;

  &:hover,
  &:active,
  &:focus,
  &:focus-visible {
    background: none;
  }

  &:hover,
  &:focus-visible,
  &.active {
    color: var(--primary);

    .side-menu__row-label {
      color: var(--primary);
      border-bottom-color: var(--primary);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
}

/* router-link variant */
.side-menu__row--link {
  display: flex;
}

/* Leading icon: fixed-width slot */
.side-menu__row-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  flex-shrink: 0;
  min-width: 0;
  font-size: 1rem;
}

/* Label: sized to its content */
.side-menu__row-label {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-bottom: 2px solid transparent;
}

// ---------------------------------------------------------------------------
// Modal content reset
// ---------------------------------------------------------------------------

.game-navbar__modal-content {
  margin: 1rem auto;
  width: 100%;
  overflow: visible;
  padding: 0;
}

// ---------------------------------------------------------------------------
// Confirmation dialog content + action buttons layout
// ---------------------------------------------------------------------------

// Inset the dialog content from the modal edges. The host Modal uses the
// `chopped` clip-path shape, which trims the corners, so the slot content needs
// its own breathing room beyond the Modal's base padding.
.game-navbar__confirm-dialog {
  max-width: 85vw;
}

.game-navbar__confirm-actions {
  display: flex;
  flex-direction: column;
  gap: $base-spacing;
  margin-top: $base-spacing;
}

// ---------------------------------------------------------------------------
// Miscellaneous layout helpers (from legacy GameNavbar.scss)
// ---------------------------------------------------------------------------

.slide-up {
  transform: translateY(-100%);
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.5s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
}

.game-navbar__mobile-nav {
  @extend %flex;
  flex-direction: column;
  justify-content: flex-end;
}
</style>
